import { useEffect } from 'react';

import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import dayjs from 'dayjs';
import type { FieldErrors, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { string, object } from 'zod/v4';

import { appConfig } from '@/config/appConfig';
import appConstants from '@/constants/appConstants';
import type { Country } from '@/features/countries/types/Country';
import type { FleetSize } from '@/features/fleetSizes/types/FleetSize';
import type { Industry } from '@/features/industries/types/Industry';
import useRegexPatterns from '@/features/regexPatterns/hooks/useRegexPatterns';
import { getValidationErrorMessage } from '@/helpers';
import useI18n from '@/hooks/useI18n';
import useNavigation from '@/hooks/useNavigation';
import usePost from '@/hooks/usePost';
import type { ErrorResponse } from '@/types/ErrorResponse';

import { authRoutes } from '../routes/authRoutes';
import type { RegisterRequestBody } from '../types/RegisterRequestBody';
import type { RegisterResponse } from '../types/RegisterResponse';

import useAuthStore from './useAuthStore';

export type FormValues = {
    fullName: string;
    emailAddress: string;
    phone: string;
    organizationName: string;
    country: Country | null;
    industry: Industry | null;
    fleetSize: FleetSize | null;
    password: string;
};

type UseRegisterReturn = {
    isValid: boolean;
    isLoading: boolean;
    response: RegisterResponse | null;
    error: ErrorResponse | null;
    validationErrors: FieldErrors<FormValues>;
    submit: () => void;
    register: UseFormRegister<FormValues>;
    setValue: UseFormSetValue<FormValues>;
    watch: UseFormWatch<FormValues>;
};

export default function useRegister(): UseRegisterReturn {
    // ----------------------------------------------------------------------------------------------------
    // MARK: States & Constants
    // ----------------------------------------------------------------------------------------------------
    const { t } = useI18n();
    const { replace } = useNavigation();
    const { regexPatterns } = useRegexPatterns();

    const updateUser = useAuthStore((state) => state.updateUser);
    const updateToken = useAuthStore((state) => state.updateToken);
    const { submit: submitHandler, response, isLoading, error, validationErrors } = usePost<RegisterResponse, ErrorResponse>();

    const schema = object({
        fullName: string()
            .nonempty({ message: t('Auth.Validations.Full_Name_Required') })
            .min(4, { message: t('Auth.Validations.Full_Name_Minimum_Length') })
            .max(255, { message: t('Auth.Validations.Full_Name_Maximum_Length') }),
        emailAddress: string()
            .nonempty({ message: t('Auth.Validations.Email_Required') })
            .email({ message: t('Auth.Validations.Email_Invalid') })
            .min(4, { message: t('Auth.Validations.Email_Minimum_Length') })
            .max(255, { message: t('Auth.Validations.Email_Maximum_Length') }),

        phone: string()
            .nonempty({ message: t('Auth.Validations.Phone_Required') })
            .min(4, { message: t('Auth.Validations.Phone_Minimum_Length') })
            .max(255, { message: t('Auth.Validations.Phone_Maximum_Length') }),
        organizationName: string()
            .nonempty({ message: t('Auth.Validations.Organization_Name_Required') })
            .min(4, { message: t('Auth.Validations.Organization_Name_Minimum_Length') })
            .max(255, { message: t('Auth.Validations.Organization_Name_Maximum_Length') }),
        password: string()
            .nonempty({ message: t('Auth.Validations.Password_Required') })
            .min(8, { message: t('Auth.Validations.Password_Minimum_Length') })
            .max(64, { message: t('Auth.Validations.Password_Maximum_Length') })
            .regex(regexPatterns?.users.password ?? /^/, {
                message: t('Auth.Validations.Password_Regex_Error'),
            }),
        country: object<Country>(),
        industry: object<Industry>(),
        fleetSize: object<FleetSize>(),
    });

    const {
        register,
        formState: { errors, isValid },
        setError,
        watch,
        setValue,
    } = useForm({
        resolver: standardSchemaResolver(schema),
        mode: appConfig.formValidation.mode,
        defaultValues: {
            fullName: '',
            emailAddress: '',
            phone: '',
            password: '',
            organizationName: '',
            country: null as Country | null,
            industry: null as Industry | null,
            fleetSize: null as FleetSize | null,
        },
    });

    const { fullName, emailAddress, phone, organizationName, country, industry, fleetSize, password } = watch();

    // ----------------------------------------------------------------------------------------------------
    // MARK: Functions
    // ----------------------------------------------------------------------------------------------------
    function submit(): void {
        const requestBody: RegisterRequestBody = {
            fullName,
            email: emailAddress.toLowerCase(),
            phone: `+${country?.phone_code}${phone}`,
            organizationName,
            countryId: country?.id ?? 0,
            industryId: industry?.id ?? 0,
            fleetSizeId: fleetSize?.id ?? 0,
            password,
        };

        submitHandler(authRoutes.api.register(), requestBody, {
            onSuccess: (res) => {
                updateUser(res.user, { expires: dayjs().add(7, 'days').toDate() });
                updateToken(res.token, { expires: dayjs().add(7, 'days').toDate() });
                localStorage.setItem(appConstants.otpCountdownStorageName, appConfig.env.otpCountdown.toString());
                setTimeout(() => {
                    replace(authRoutes.app.registerVerification());
                }, 500);
            },
        });
    }

    // ----------------------------------------------------------------------------------------------------
    // MARK: Side Effects
    // ----------------------------------------------------------------------------------------------------
    useEffect(() => {
        if (validationErrors.length > 0) {
            setError('fullName', { message: getValidationErrorMessage('full_name', validationErrors) });
            setError('emailAddress', { message: getValidationErrorMessage('email', validationErrors) });
            setError('phone', { message: getValidationErrorMessage('phone', validationErrors) });
            setError('organizationName', { message: getValidationErrorMessage('organization_name', validationErrors) });
            setError('password', { message: getValidationErrorMessage('password', validationErrors) });
            setError('country', { message: getValidationErrorMessage('country_id', validationErrors) });
            setError('industry', { message: getValidationErrorMessage('industry_id', validationErrors) });
            setError('fleetSize', { message: getValidationErrorMessage('fleet_size_id', validationErrors) });
        }
    }, [validationErrors, setError]);

    // ----------------------------------------------------------------------------------------------------
    // MARK: Return
    // ----------------------------------------------------------------------------------------------------
    return {
        isValid,
        isLoading,
        response,
        error,
        validationErrors: errors,
        submit,
        register,
        setValue,
        watch,
    };
}
